import { TIME_STEP_DURATION_IN_MN } from '@features/components/visualization/timeline/models/timeline';

export class TimelineUtils {
  /**
   * Ceil the date so that a time step falls on midnight
   *
   * @param {Date} date
   * @param {number} timeStepOptionPos
   * @returns {Date}
   */
  public static ceilDate(date: Date, timeStepOptionPos: number) {
    const timeStepDurationInMin = TIME_STEP_DURATION_IN_MN[timeStepOptionPos];

    if (timeStepOptionPos < 6) {
      const timeStepDurationInMs = this.getTimeStepDurationInMs(timeStepOptionPos);
      const time = date.getTime();
      return new Date(Math.ceil(time / timeStepDurationInMs) * timeStepDurationInMs - 1);
    } else {
      const ceilDate = new Date(date);
      const timeStepDurationInHour = timeStepDurationInMin / 60;
      const hour =
        ceilDate.getHours() +
        ceilDate.getMinutes() / 60 +
        ceilDate.getSeconds() / 3600 +
        ceilDate.getMilliseconds() / (3600 * 1000);
      const ceilHour = Math.ceil(hour / timeStepDurationInHour) * timeStepDurationInHour;
      ceilDate.setHours(ceilHour - 1, 59, 59, 999);
      return ceilDate;
    }
  }

  /**
   * Returns the starting date of a time step based on the given parameters.
   *
   * @param {number} timeStep - The time step index (0-based).
   * @param {Date} startDate - The starting date of the timeline.
   * @param {number} timeStepOptionPos - The position of the time step option in the
   *   predefined durations array.
   * @returns {Date} - The calculated date for the given time step.
   */
  public static dateOfStep(
    timeStep: number,
    startDate: Date,
    timeStepOptionPos: number
  ): Date {
    const timeStepDurationInMs = this.getTimeStepDurationInMs(timeStepOptionPos);
    return new Date(startDate.getTime() + timeStep * timeStepDurationInMs);
  }

  /**
   * Convert a date to the starting date of the time step it belongs to.
   *
   * @param {Date} date - The date to convert.
   * @param {Date} startDate - The starting date of the timeline.
   * @param {Date} endDate - The ending date of the timeline.
   * @param {number} timeStepOptionPos - The position of the time step option selected.
   * @returns {number} - The index of the time step that the date belongs to.
   */
  public static dateToStep(
    date: Date,
    startDate: Date,
    endDate: Date,
    timeStepOptionPos: number
  ): number {
    if (date < startDate && date > endDate) {
      return -1;
    }

    const timeStepDurationInMs = this.getTimeStepDurationInMs(timeStepOptionPos);
    return (date.getTime() - startDate.getTime()) / timeStepDurationInMs;
  }

  /**
   * Floor the date so that a time step falls on midnight
   *
   * @param {Date} date
   * @param {number} timeStepOptionPos
   * @returns {Date}
   */
  public static floorDate(date: Date, timeStepOptionPos: number): Date {
    const timeStepDurationInMs = this.getTimeStepDurationInMs(timeStepOptionPos);

    if (timeStepOptionPos < 6) {
      const time = date.getTime();
      return new Date(Math.floor(time / timeStepDurationInMs) * timeStepDurationInMs);
    } else {
      const floorDate = new Date(date);
      const timeStepDurationInHour = timeStepDurationInMs / (60 * 60 * 1000);
      const hour = floorDate.getHours();
      const floorHour = Math.floor(hour / timeStepDurationInHour) * timeStepDurationInHour;
      floorDate.setHours(floorHour, 0, 0, 0);
      return floorDate;
    }
  }

  /**
   * Calculates the duration of a time step in milliseconds based on its position
   *
   * @private
   * @param {number} timeStepOptionPos - The position of the time step option in the
   *   predefined durations array.
   * @returns {number} - The duration of the time step in milliseconds.
   */
  private static getTimeStepDurationInMs(timeStepOptionPos: number): number {
    return TIME_STEP_DURATION_IN_MN[timeStepOptionPos] * 60 * 1000;
  }

  /**
   * Converts a temperature in degrees Celsius to a color string following a typical
   * temperature gradient:
   *
   * - Below -10°C: Deep blue
   * - -10°C to 0°C: Light blue to cyan
   * - 0°C to 15°C: Cyan to green
   * - 15°C to 25°C: Green to yellow
   * - 25°C to 35°C: Yellow to orange
   * - Above 35°C: Orange to red
   *
   * @param tempCelsius - Temperature in degrees Celsius
   * @returns RGB color string in format "rgb(r, g, b)"
   */
  static temperatureToColor(tempCelsius: number): string {
    // Define temperature breakpoints and their corresponding RGB colors
    const colorStops: Array<{ temp: number; rgb: [number, number, number] }> = [
      { temp: -20, rgb: [40, 70, 150] }, // Deep blue
      { temp: -10, rgb: [60, 100, 190] }, // Blue
      { temp: 0, rgb: [80, 150, 220] }, // Light blue
      { temp: 4, rgb: [100, 200, 220] }, // Cyan
      { temp: 18, rgb: [250, 220, 80] }, // Yellow
      { temp: 25, rgb: [255, 180, 50] }, // Light orange
      { temp: 32, rgb: [255, 120, 50] }, // Orange
      { temp: 38, rgb: [255, 80, 60] }, // Red-orange
      { temp: 45, rgb: [220, 50, 50] } // Red
    ];

    // Handle extreme cold
    if (tempCelsius <= colorStops[0].temp) {
      const [r, g, b] = colorStops[0].rgb;
      return `rgb(${r}, ${g}, ${b})`;
    }

    // Handle extreme heat
    if (tempCelsius >= colorStops[colorStops.length - 1].temp) {
      const [r, g, b] = colorStops[colorStops.length - 1].rgb;
      return `rgb(${r}, ${g}, ${b})`;
    }

    // Find the two color stops to interpolate between
    let lowerStop = colorStops[0];
    let upperStop = colorStops[1];

    for (let i = 0; i < colorStops.length - 1; i++) {
      if (tempCelsius >= colorStops[i].temp && tempCelsius <= colorStops[i + 1].temp) {
        lowerStop = colorStops[i];
        upperStop = colorStops[i + 1];
        break;
      }
    }

    // Calculate interpolation factor (0 to 1)
    const tempRange = upperStop.temp - lowerStop.temp;
    const factor = (tempCelsius - lowerStop.temp) / tempRange;

    // Interpolate RGB values
    const r = Math.round(lowerStop.rgb[0] + (upperStop.rgb[0] - lowerStop.rgb[0]) * factor);
    const g = Math.round(lowerStop.rgb[1] + (upperStop.rgb[1] - lowerStop.rgb[1]) * factor);
    const b = Math.round(lowerStop.rgb[2] + (upperStop.rgb[2] - lowerStop.rgb[2]) * factor);

    return `rgb(${r}, ${g}, ${b})`;
  }
}
